/* eslint-disable max-lines */
import React, { useEffect, useState } from 'react';
import { useTranslate } from '@im/react-localization';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import { usePNAetaWarehouseDetails } from '../use-eta-mlStockData';
import { WarehouseDetails, Warehouse } from '../internal/common.types';
import { WarehouseETAGridDetails } from './internalETA/commonETAType';
import { TableSkeleton } from '@im/ui-lib/TableSkeleton';
import { useDecodedToken } from 'mf_core/auth';
import WarehouseETAGridData from './WarehouseETAGridData';

type WarehouseEMEADetailsProps = {
  displayOutOfStockFlag?: boolean;
  fromPage: string;
  id: any;
  licenseSelectorVendorSubrange?: string;
  onClose: () => void;
  onOrderValue?: number | false;
  open: boolean;
  reserveFlag: boolean;
  reservedStock: number;
  sku: string;
  skuType?: string;
  stockStatus: string;
  vendorCode?: string;
  warehouseDetails: WarehouseDetails[];
  warehouses?: Warehouse[];
};

export function WarehouseETADetailsModel(props: WarehouseEMEADetailsProps) {
  const {
    displayOutOfStockFlag,
    fromPage,
    onClose,
    onOrderValue,
    open,
    reservedStock,
    sku,
    skuType,
    vendorCode,
    warehouseDetails,
  } = props;
  const translate = useTranslate();
  const decodedToken = useDecodedToken();
  const siteCode = decodedToken?.siteCode;
  const [warehouseStockDetailUpdate, setwarehouseStockDetailUpdate] = useState<
    WarehouseETAGridDetails[]
  >([]);
  const [isInitialRender, setIsInitialRender] = useState(true);
  const { data, isLoading } = usePNAetaWarehouseDetails(
    sku,
    skuType,
    vendorCode,
    true
  );
  const inProductsML = data?.[0]?.preOrderStockDetails;

  useEffect(() => {
    if (
      isInitialRender &&
      inProductsML !== undefined &&
      inProductsML?.length > 0 &&
      !isLoading
    ) {
      setIsInitialRender(false);
      if (inProductsML) {
        const warehouseETAInStockList = inProductsML
          .filter(
            (x: any) =>
              x.quantity !== undefined &&
              x.quantity !== null &&
              x.quantity > 0 &&
              x.stockType === 'INSTOCK'
          )
          .sort((a: any, b: any) =>
            a.leadDaysLowerBoundary > b.leadDaysLowerBoundary ? 1 : -1
          )
          .map((inStock: any, i: number) => {
            return (
              inStock.stockType === 'INSTOCK' && inStock.quantity > 0
                ? {
                    centralFlag: inStock.leadDaysLowerBoundary === 7,
                    eTShipmenttext: `${translate(
                      'Warehouse.ETAGridInStockText'
                    )} ${inStock.leadDaysLowerBoundary}-${
                      inStock.leadDaysUpperBoundary
                    } ${translate('Warehouse.ETAGridOnOrderText')}`,
                    eta: inStock.eta,
                    onDemandFlag: false,
                    quantity: inStock.quantity,
                    stockStatues: 'INSTOCK',
                    stockStatus: translate('Warehouse.ETAGridStatusInStock'),
                    stockStatusDisplayFlag:
                      i === 0 && !(inStock.reservedQuantity > 0),
                  }
                : {
                    centralFlag: null,
                    eTShipmenttext: null,
                    eta: null,
                    onDemandFlag: false,
                    quantity: null,
                    stockStatues: null,
                    stockStatus: null,
                    stockStatusDisplayFlag: false,
                  }
            ) as WarehouseETAGridDetails;
          });

        const warehouseETAReservedInStockList = inProductsML
          .filter(
            (x: any) => x.reservedQuantity > 0 && x.stockType === 'INSTOCK'
          )
          .map((inStock: any, i: number) => {
            return (
              inStock.stockType === 'INSTOCK' && inStock.reservedQuantity > 0
                ? {
                    centralFlag: inStock.leadDaysLowerBoundary === 7,
                    eTShipmenttext: `${translate(
                      'Warehouse.ETAGridInStockText'
                    )} ${inStock.leadDaysLowerBoundary}-${
                      inStock.leadDaysUpperBoundary
                    } ${translate('Warehouse.ETAGridOnOrderText')}`,
                    eta: inStock.eta,
                    onDemandFlag: false,
                    quantity: inStock.reservedQuantity,
                    stockStatues: 'INSTOCK',
                    stockStatus: translate('Warehouse.ETAGridStatusInStock'),
                    stockStatusDisplayFlag: inStock.reservedQuantity > 0,
                  }
                : {
                    centralFlag: null,
                    eTShipmenttext: null,
                    eta: null,
                    onDemandFlag: false,
                    quantity: null,
                    stockStatues: null,
                    stockStatus: null,
                    stockStatusDisplayFlag: false,
                  }
            ) as WarehouseETAGridDetails;
          });

        const warehouseETAOnOrderListData = inProductsML
          .filter(
            (x: any) =>
              ((x.quantity !== null && x.quantity > 0) ||
                (x.reservedQuantity !== null && x.reservedQuantity > 0)) &&
              x.stockType === 'ONORDER'
          )
          ?.map((onOrder: any, i: number) => {
            return (
              onOrder.stockType === 'ONORDER' &&
              (onOrder.quantity > 0 || onOrder.reservedQuantity > 0)
                ? {
                    centralFlag: false,
                    eTShipmenttext: '',
                    eta: onOrder.eta,
                    onDemandFlag: false,
                    quantity:
                      onOrder.quantity > 0
                        ? onOrder.quantity
                        : onOrder.reservedQuantity,
                    reservedQuantity: onOrder.reservedQuantity,
                    stockStatues: 'ONORDER',
                    stockStatus: translate('Warehouse.ETAGridStatusOnOrder'),
                    stockStatusDisplayFlag: i === 0,
                  }
                : {
                    centralFlag: null,
                    eTShipmenttext: null,
                    eta: null,
                    onDemandFlag: false,
                    quantity: null,
                    reservedQuantity: null,
                    stockStatues: null,
                    stockStatus: null,
                    stockStatusDisplayFlag: false,
                  }
            ) as WarehouseETAGridDetails;
          });

        const warehouseETAOnOrderList = warehouseETAOnOrderListData?.reduce(
          (acc: any, obj: any) => {
            const existObj = acc.find((item: any) => item.eta === obj.eta);
            if (existObj) {
              existObj.quantity = (
                parseInt(existObj.quantity) + parseInt(obj.quantity)
              ).toString();
              return acc;
            }
            acc.push(obj);
            return acc;
          },
          []
        );

        const inStock_OnOrderList =
          warehouseETAInStockList !== null &&
          warehouseETAInStockList !== undefined &&
          warehouseETAOnOrderList !== null &&
          warehouseETAOnOrderList !== undefined
            ? warehouseETAReservedInStockList
                .filter((x: any) => x.quantity !== null && x.quantity > 0)
                .concat(
                  warehouseETAInStockList
                    .filter(
                      (x: any) =>
                        x.quantity !== undefined &&
                        x.quantity !== null &&
                        x.quantity > 0
                    )
                    .concat(
                      warehouseETAOnOrderList.filter(
                        (x: any) => x.quantity !== null && x.quantity > 0
                      )
                    )
                )
            : warehouseETAInStockList.filter(
                (x: any) =>
                  x.quantity !== undefined &&
                  x.quantity !== null &&
                  x.quantity > 0
              );

        const outOfStock =
          displayOutOfStockFlag &&
          inProductsML.map((inStock: any, i: number) => {
            return {
              centralFlag: undefined,
              eTShipmenttext: '-',
              eta: '-',
              onDemandFlag: i === 0,
              quantity: '-',
              stockStatus: translate('Warehouse.ETAGridOnOutOStockfText'),
              stockStatusDisplayFlag: true,
            } as WarehouseETAGridDetails;
          });

        setwarehouseStockDetailUpdate(
          (inStock_OnOrderList?.length > 0
            ? inStock_OnOrderList
            : outOfStock.filter((x: any) => x.onDemandFlag === true)) as []
        );
      }
    } else if (
      (inProductsML === undefined || inProductsML?.length === 0) &&
      displayOutOfStockFlag &&
      onOrderValue === 0 &&
      !isLoading
    ) {
      setIsInitialRender(false);
      const outOfStock = [
        {
          centralFlag: undefined,
          eTShipmenttext: '-',
          eta: '-',
          onDemandFlag: true,
          quantity: '-',
          stockStatus: translate('Warehouse.ETAGridOnOutOStockfText'),
          stockStatusDisplayFlag: true,
        } as WarehouseETAGridDetails,
      ];
      setwarehouseStockDetailUpdate(outOfStock as []);
    }
  }, [
    inProductsML,
    isInitialRender,
    isLoading,
    displayOutOfStockFlag,
    onOrderValue,
    translate,
    warehouseDetails,
    warehouseStockDetailUpdate,
  ]);

  return (
    <>
      <Modal onClose={onClose} open={open}>
        {isLoading ? (
          <RootBox>
            <TableSkeleton
              columns={[
                '----------------------------------------------------------------------------',
                '--------------------',
                '-------------------------------------------------',
                '--------------------',
                '------------------------------------',
                '----------------------------------------------------------',
              ]}
              key={sku}
              rows={4}
            />
          </RootBox>
        ) : (
          <RootBox>
            <Typography
              sx={{
                display: 'flex',
                fontSize: '19px',
                justifyContent: 'space-between',
                width: '900px',
              }}
              variant="h5"
            >
              {translate('Warehouse.ETAPopupName')}
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>{' '}
            </Typography>
            <WarehouseETAGridData
              fromPage={fromPage}
              reservedStock={reservedStock}
              siteCode={siteCode}
              skuType={skuType}
              stockStatus={props.stockStatus}
              warehouseStockDetailUpdate={warehouseStockDetailUpdate}
            ></WarehouseETAGridData>
          </RootBox>
        )}
      </Modal>
    </>
  );
}

const RootBox = styled(Box)(({ theme }: { theme: any }) => ({
  background: theme.palette.background.paper,
  borderRadius: 5,
  boxShadow: '24px solid #000',
  display: 'flex',
  flexDirection: 'column',
  left: '50%',
  outline: 'none',
  padding: theme.spacing(3),
  position: 'absolute',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  width: 'auto',
}));
